import Actions from 'flow/actions';
import OmniAgentResource from 'flow/api/omniagent';
import { addOmniChannelLogEntry } from 'flow/utils/browser-utils';
import { getCentionDataFromRtcSession } from 'flow/utils/omnichannel-utils';
import {
    spawnIncomingOmnichannelErrandNotification,
    spawnOmnichannelPushNotificationReceived,
} from 'flow/actions/app-actions';
import { navigateTo } from 'flow/actions/navigate-actions';
import { convertNumberToE164 } from 'flow/utils/call-utils';
import { getOwnExtensionKey } from 'flow/selectors/selectors';
import { getIdFromEntityKey } from 'flow/utils';
import Enum from 'enum';

export const OMNICHANNEL_PROPERTIES = new Enum({
    DEEP_LINK: 'deepLink',
    DESCRIPTION: 'humanReadableDesc',
    CHANNEL_TYPE: 'channel',
});

export const routeUserToOmnichannelErrand =
    (deepLinkId, contextForLog) => (dispatch) => {
        addOmniChannelLogEntry(
            `routing user to omnichannel errand with deeplink id ${deepLinkId} via click on ${contextForLog}`,
        );
        dispatch(setOmniChannelDeepLinkId(deepLinkId));
        dispatch(navigateTo('/omni'));
    };

export const onNewOmnichannelSession = (session) => (dispatch) => {
    const deepLinkId = getCentionDataFromRtcSession(
        session,
        OMNICHANNEL_PROPERTIES.DEEP_LINK.value,
    );
    const description = getCentionDataFromRtcSession(
        session,
        OMNICHANNEL_PROPERTIES.DESCRIPTION.value,
    );
    const channelType = getCentionDataFromRtcSession(
        session,
        OMNICHANNEL_PROPERTIES.CHANNEL_TYPE.value,
    );

    addOmniChannelLogEntry(
        'Incoming omnichannel errand with deepLinkId: ',
        deepLinkId,
    );
    dispatch(
        spawnIncomingOmnichannelErrandNotification(
            session,
            deepLinkId,
            description,
            channelType,
        ),
    );
};

export const setOmniChannelDeepLinkId = (deepLinkId) => ({
    type: Actions.SET_OMNICHANNEL_DEEPLINK_ID,
    deepLinkId: deepLinkId,
});

export const setUserOwnedOmnichannelErrandsAwaitingActivityCount = (
    errandsCount,
) => ({
    type: Actions.SET_USER_OWNED_OMNICHANNEL_ERRANDS_AWAITING_ACTIVITY_COUNT,
    errandsCount,
});

export const setOmnichannelIframeReference = (iframe) => ({
    type: Actions.SET_OMNICHANNEL_IFRAME_REF,
    iframeRef: iframe,
});

const setCentionDeeplinkForCallerId = (deeplink, callerId) => ({
    type: Actions.SET_CENTION_DEEPLINK_FOR_CALLER_ID,
    callerId,
    deeplink,
});

const removeCentionDeeplinkForCallerId = (callerId) => ({
    type: Actions.REMOVE_CENTION_DEEPLINK_FOR_CALLER_ID,
    callerId,
});

export const fetchAuthDataWithoutSettingDataInStore = (extensionKey) => {
    return new Promise((resolve, reject) => {
        OmniAgentResource.getOmniAgent(
            {
                onResult(json) {
                    resolve(json);
                },
                onClientError: (error) => {
                    reject(error);
                },
                onError: (error) => {
                    reject(error);
                },
            },
            extensionKey,
        );
    });
};

export const onReceivedTopEventPushOfTypeOmniErrand =
    (msg) => (dispatch, getState) => {
        const state = getState();
        const messageData = msg.data;
        const { deeplink, callerId } = messageData;
        const accountIdReceivedString =
            messageData.accountId && messageData.accountId.toString();

        const ownAccountId = getIdFromEntityKey(getOwnExtensionKey(state));

        if (ownAccountId !== accountIdReceivedString) {
            addOmniChannelLogEntry(
                `Not doing anything with deeplink ${deeplink} because logged in user accountId ${ownAccountId} does not match accountId in data received ${accountIdReceivedString}`,
            );
            return;
        }

        const callerIdE164 = convertNumberToE164(callerId);
        addOmniChannelLogEntry(
            `Setting deeplink ${deeplink} for call with callerId ${callerIdE164}`,
        );
        dispatch(setCentionDeeplinkForCallerId(deeplink, callerIdE164));
    };

const getAssignedOmniErrandActionStringUserFriendly = (actionString) => {
    switch (actionString) {
        case 'reopen':
            return 'Ticket reopened';
        case 'assigned':
            return 'Ticket assigned to you';
        case 'new_task':
            return 'New task';
        default:
            return undefined;
    }
};

export const onReceivedTopEventPushOfTypeAssignedOmniErrand =
    (msg) => (dispatch, getState) => {
        const state = getState();
        const messageData = msg.data;
        const { deeplink, action, accountId } = messageData;
        const accountIdReceivedString = accountId && accountId.toString();

        const ownAccountId = getIdFromEntityKey(getOwnExtensionKey(state));

        if (ownAccountId !== accountIdReceivedString) {
            addOmniChannelLogEntry(
                `Not doing anything with deeplink ${deeplink} because logged in user accountId ${ownAccountId} does not match accountId in data received ${accountIdReceivedString}`,
            );
            return;
        }

        const actionStringUserFriendly =
            getAssignedOmniErrandActionStringUserFriendly(action);
        if (!actionStringUserFriendly) {
            addOmniChannelLogEntry(
                `Action string ${actionStringUserFriendly} not recognized`,
            );
            return;
        }
        //Do nothing more with actionStringUserFriendlyForNow

        dispatch(spawnOmnichannelPushNotificationReceived(deeplink));
    };

export const removeOmnichannelDeeplink =
    (topMessageUniqueId) => (dispatch, getState) => {
        const { top } = getState();
        const channel = top.getIn(['uniqueIdToChannel', topMessageUniqueId]);
        const callerNumber =
            channel &&
            (channel.get('OriginalCallerID') || channel.get('Source'));

        if (!callerNumber) return;

        const callerNumberE164 = convertNumberToE164(callerNumber);
        dispatch(removeCentionDeeplinkForCallerId(callerNumberE164));
    };
